import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toThunkRejectValue } from "../../../Common/isRequestCanceled";
import { getAuthDetails } from "../../../Common/cookiesHelper";
import { getSessionAuthHeader, getSessionFields, withSessionFields } from "../../../Common/sessionAuth";
import {
  BASE_URL,
  CREATE_GROUP_API,
  FETCH_GROUP_API,
  GET_STORE_SETUP_LIST,
  GROUP_JOB_STATUS_API,
  UPDATE_GROUP_API,
} from "../../../Constants/Config";
import { ciUrl } from "../../../Constants/nodeCi";

const initialState = {
  loading: false,
  storesetupData: [],
  successMessage: "",
  error: "",
};

const GROUP_JOB_POLL_INTERVAL_MS = 3000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Polls until the job reports success ("1") or failure ("2"). Group syncs can
// run long, so there is no attempt cap — transient network errors are retried
// instead of aborting a job that is still running on the server.
const pollGroupJobStatus = async (jobId) => {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    let body;
    try {
      const response = await axios.get(BASE_URL + GROUP_JOB_STATUS_API, {
        params: { ...getSessionFields(), job_id: jobId },
        headers: getSessionAuthHeader(),
      });
      body = response.data;
    } catch {
      await sleep(GROUP_JOB_POLL_INTERVAL_MS);
      continue;
    }

    if (!body?.status) {
      return {
        status: false,
        message: body?.message || "Unable to fetch group sync status.",
        data: body?.data,
      };
    }

    const jobStatus = String(body?.data?.job_status ?? "");

    if (jobStatus === "1") {
      return {
        status: true,
        message: body.message || "Group sync completed successfully.",
        data: body.data,
      };
    }

    if (jobStatus === "2") {
      return {
        status: false,
        message: body.message || "Group sync failed.",
        data: body.data,
      };
    }

    await sleep(GROUP_JOB_POLL_INTERVAL_MS);
  }
};

// Generate pening , fulfilled and rejected action type
export const fetchStoreSettingSetupData = createAsyncThunk(
  "SettingSetupSlice/fetchStoreSettingSetupData.",
  async (data, { rejectWithValue }) => {
    const { token, ...storeData } = data;
    try {
      const response = await axios.post(
        ciUrl(GET_STORE_SETUP_LIST),
        storeData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // console.log(response)
      if (response.data.status === true) {
        return response.data.result;
      }
    } catch (error) {
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const submitCreateGroupForm = createAsyncThunk(
  "products/submitCreateGroupForm",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        BASE_URL + (payload.isEdit ? UPDATE_GROUP_API : CREATE_GROUP_API),
        withSessionFields(payload.formData),
        {
          headers: {
            // "Content-Type": "multipart/form-data",
            ...getSessionAuthHeader(),
          },
        }
      );

      const asyncResponse = response.data;
      if (!asyncResponse?.status) {
        return asyncResponse;
      }

      const jobId = asyncResponse?.data?.job_id;
      if (!jobId) {
        return {
          status: false,
          message:
            asyncResponse.message || "Job ID not returned from the server.",
        };
      }

      return pollGroupJobStatus(jobId);
    } catch (error) {
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

function attachLoginMerchantId(requestData) {
  let loginMerchantId = "";
  try {
    loginMerchantId =
      getAuthDetails()?.LoginGetDashBoardRecordJson?.data?.merchant_id || "";
  } catch (e) {
    loginMerchantId = "";
  }
  if (!loginMerchantId) return requestData;

  if (requestData instanceof FormData) {
    if (!requestData.has("login_merchant_id")) {
      requestData.append("login_merchant_id", loginMerchantId);
    }
    return requestData;
  }

  if (requestData && typeof requestData === "object") {
    if (!requestData.login_merchant_id) {
      return { ...requestData, login_merchant_id: loginMerchantId };
    }
  }

  return requestData;
}

export const fetchGroupAPI = createAsyncThunk(
  "products/fetchGroupAPI",
  async (payload, { rejectWithValue }) => {
    try {
      const requestData = attachLoginMerchantId(payload?.formData || payload);
      const cancelToken = payload?.cancelToken;
      const response = await axios.post(BASE_URL + FETCH_GROUP_API, withSessionFields(requestData), {
        headers: getSessionAuthHeader(),
        ...(cancelToken ? { cancelToken } : {}),
      });
      // console.log(response)
      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue({ cancelled: true, message: "Request cancelled" });
      }
      // throw new Error("Internal Server Error");
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

const SettingSetupSlice = createSlice({
  name: "StoreSettingsetupList",
  initialState,
  extraReducers: (builder) => {
    builder.addCase(fetchStoreSettingSetupData.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchStoreSettingSetupData.fulfilled, (state, action) => {
      state.loading = false;
      state.storesetupData = action.payload;
      state.error = "";
    });
    builder.addCase(fetchStoreSettingSetupData.rejected, (state, action) => {
      state.loading = false;
      state.storesetupData = {};
      state.error = action.error.message;
    });
  },
});

export default SettingSetupSlice.reducer;
